import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Lightning, X, Check, CircleNotch } from "@phosphor-icons/react";

const ACCENT = "#FFD60A";

export default function TopUpModal({ open, onClose, onSuccess }) {
  const [packs, setPacks] = useState([]);
  const [isMember, setIsMember] = useState(false);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    if (open) {
      api.get("/billing/topup/packs").then((r) => {
        setPacks(r.data.packs || []);
        setIsMember(!!r.data.is_member);
      }).catch(() => { setPacks([]); setIsMember(false); });
    }
  }, [open]);

  if (!open) return null;

  const buy = async (packId) => {
    setBuying(packId);
    try {
      const res = await api.post("/billing/topup", { pack_id: packId, origin_url: window.location.origin });
      if (res.data.url) { window.location.href = res.data.url; return; }
      toast.error("Could not start checkout. Please try again.");
      setBuying(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBuying(null);
    }
  };

  return (
    <div data-testid="topup-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-3xl border border-white/10 bg-[#0E0E0E] p-7 shadow-2xl">
        <button data-testid="topup-close" onClick={onClose} className="absolute top-5 right-5 text-zinc-500 hover:text-white transition-colors"><X size={20} /></button>
        <div className="flex items-center gap-2 mb-1">
          <Lightning weight="fill" size={22} style={{ color: ACCENT }} />
          <h2 className="font-display font-black text-2xl tracking-tight">Need more credits?</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-6">One-time top-ups that never expire — they kick in after your monthly allowance runs out.</p>

        <div className="grid sm:grid-cols-2 gap-4">
          {packs.map((p) => {
            const popular = p.id === "B";
            const full = Number(p.amount_full ?? p.amount ?? 0);
            const member = Number(p.amount_member ?? p.amount ?? 0);
            const chargedNow = isMember ? member : full;
            return (
              <div key={p.id} data-testid={`topup-pack-${p.id}`} className={`relative rounded-2xl p-6 flex flex-col ${popular ? "bg-[#161616]" : "bg-[#121212] border border-white/10"}`} style={popular ? { border: `2px solid ${ACCENT}` } : {}}>
                {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full text-black" style={{ backgroundColor: ACCENT }}>Best value</span>}
                <p className="font-display font-black text-3xl tracking-tighter">{p.credits}</p>
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-4">credits</p>
                {/* Pricing block: anchor (full) struck through, member price highlighted */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-zinc-500 line-through text-base" data-testid={`topup-anchor-${p.id}`}>${full.toFixed(0)}</span>
                    <span className="font-black text-2xl text-white" data-testid={`topup-member-${p.id}`}>${member.toFixed(0)}</span>
                    <span className="text-[11px] text-zinc-400 font-medium">for members</span>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1">
                    {isMember
                      ? "You're a subscriber — the member price is applied automatically."
                      : "Subscribe (from $15/mo) to unlock the member price on every top-up."}
                  </p>
                </div>
                <button data-testid={`topup-buy-${p.id}`} onClick={() => buy(p.id)} disabled={!!buying}
                  className="mt-auto w-full py-2.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] disabled:opacity-60"
                  style={{ backgroundColor: ACCENT, color: "#0a0a0a" }}>
                  {buying === p.id ? <CircleNotch size={16} className="animate-spin" /> : <Check weight="bold" size={16} />}
                  Buy for ${chargedNow.toFixed(0)}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-center text-[11px] text-zinc-600 mt-5">Secure Stripe checkout · credits never expire · instant delivery.</p>
      </div>
    </div>
  );
}
